/**
 * MifeLife v2 — Landing page (Marketplace repositioning).
 *
 * Premium mentor marketplace. Human mentors are the primary value; AI is the
 * matching & recommendation engine. Real professional photography, no AI faces.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Search, BadgeCheck, ShieldCheck, Sparkles, Video,
  Users, CalendarCheck, Target, Star,
  Briefcase, Rocket, Code2, LineChart, Megaphone, Crown, TrendingUp, BarChart3, PenTool, Building2,
  Brain, Route, Mail, Loader2, CheckCircle2, Quote, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import V2Nav from '../../components/v2/V2Nav';
import V2Footer from '../../components/v2/V2Footer';
import { apiRequest } from '../../lib/utils';

// Real professional stock photography (Unsplash — royalty-free, real humans).
const HERO_IMG = 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=900&h=1100&fit=crop&q=80';
// Founder portrait — Hakan Dalmış (served from our own origin for prod stability).
const FOUNDER_IMG = '/founder-hakan.jpg';

const COPY = {
  tr: {
    eyebrow: 'UZMAN MENTOR PAZARYERI',
    h1: 'Kariyerinde, işinde veya girişiminde',
    h1Accent: 'sana yol gösterecek uzman mentorlarla tanış.',
    sub: "Türkiye ve Avrupa'dan deneyimli mentorlarla birebir online görüşme yap. Doğru mentoru AI ile bul, görüşmeni hemen planla.",
    ctaPrimary: 'Mentor Bul',
    ctaSecondary: 'Mentor Ol',
    trust: ['Doğrulanmış mentorlar', 'Güvenli ödeme', 'AI eşleştirme', 'Online görüşme'],
    proofKicker: 'Neden MifeLife?',
    sp1Title: 'Doğrulanmış uzman mentorlar',
    sp1Sub: 'Her mentor profili elle incelenip onaylanır.',
    sp2Title: 'Güvenli ödeme — iyzico',
    sp2Sub: '256-bit SSL. Kart bilgilerin asla bizde tutulmaz.',
    sp3Title: 'AI destekli eşleştirme',
    sp3Sub: 'Hedefine en uygun mentoru saniyeler içinde bul.',
    flowTitle: '3 adımda mentorunla görüş',
    expTitle: 'Uzmanlık alanına göre keşfet',
    expSub: 'İhtiyacın olan konuda uzmanlaşmış mentorları tek tıkla filtrele.',
    s1: 'Mentor bul', s1d: 'Uzmanlık, dil ve bütçene göre filtrele. AI sana en uygun mentorları önerir.',
    s2: 'Görüşmeni planla', s2d: 'Uygun saati seç, güvenle öde. Online görüşme linkin anında hazır.',
    s3: 'Hedefine ilerle', s3d: 'Birebir online görüşmeyle gerçek bir uzmandan yol haritanı al.',
    humanKicker: 'İNSAN ODAKLI, AI DESTEKLİ',
    humanTitle: 'Yapay zeka eşleştirir, gerçek mentorlar yol gösterir.',
    humanSub: 'MifeLife bir “AI ürünü” değil; deneyimli insan mentorların platformu. AI sadece doğru mentoru bulmana yardım eder — değer, mentorunla yaptığın görüşmede.',
    mentorsTitle: 'Öne çıkan mentorlar',
    mentorsSub: "Türkiye ve Avrupa'dan doğrulanmış uzmanlar. AI uyum skoruyla sıralanır.",
    seeAll: 'Tüm mentorleri gör',
    finalCtaTitle: 'Doğru mentor, doğru zamanda her şeyi değiştirir.',
    finalCtaSub: 'Hemen sana uygun bir uzman mentor bul, ilk görüşmeni bugün planla.',
    finalCta: 'Mentor Bul',
    // --- Founder Trust ---
    founderKicker: 'KURUCUDAN',
    founderName: 'Hakan Dalmış',
    founderRole: 'Kurucu · MifeLife',
    founderQuote: 'MifeLife’i, doğru insanla doğru zamanda tanışmanın hayatları değiştirdiğine inandığım için kurdum. AI yol haritanı çizer; ama gerçek dönüşüm, deneyimli bir mentorla yaptığın o tek görüşmede başlar.',
    founderTrust1: 'Her mentor elle incelenir ve onaylanır',
    founderTrust2: 'Ödemelerin iyzico güvencesinde',
    founderTrust3: 'Verilerin KVKK uyumlu korunur',
    // --- How it works (4 step) ---
    howTitle: 'MifeLife nasıl çalışır?',
    howSub: 'Dört basit adımda, hedefine seni götürecek mentorla tanış.',
    how1: 'Analiz', how1d: '10 soruluk kısa analizle önceliklerini ve güçlü/zayıf yönlerini çıkar.',
    how2: 'Mentör eşleştirme', how2d: 'AI, hedefine en uygun uzman mentorları senin için sıralar.',
    how3: 'Görüşme planla', how3d: 'Uygun saati seç, güvenle öde. Online görüşme linkin anında hazır.',
    how4: 'Hedefe ilerle', how4d: 'Birebir görüşmeyle gerçek bir uzmandan yol haritanı al, uygula.',
    // --- Early Access Club ---
    eaKicker: 'EARLY ACCESS CLUB',
    eaTitle: 'Kurucu topluluğa katıl',
    eaSub: 'Lansman önceliği + kurucu indirimi seni bekliyor. İlk mentor duyurularına ve yeni özelliklere herkesten önce eriş.',
    eaBenefit1: 'İlk mentor duyurularına erken erişim',
    eaBenefit2: 'Yeni özelliklerden önce haberdar olma',
    eaBenefit3: 'Kurucu topluluğa özel avantajlar',
    eaPlaceholder: 'E-posta adresin',
    eaCta: 'Kulübe katıl',
    eaSuccess: 'Aramıza hoş geldin! Onay e-postanı kontrol et.',
    eaErr: 'Geçerli bir e-posta gir.',
    discountBadge: 'İlk seans indirimi',
    eaProofMany: 'kişi erken erişim listesine katıldı',
    eaProofFew: 'Kurucu topluluğa katılan ilk üyelerden biri olun.',
    eaYourNumber: 'Kurucu üye sıran',
  },
  en: {
    eyebrow: 'EXPERT MENTOR MARKETPLACE',
    h1: 'Meet expert mentors who guide you',
    h1Accent: 'in your career, business or startup.',
    sub: 'Book 1:1 online sessions with experienced mentors from Turkey and Europe. Find the right mentor with AI, schedule instantly.',
    ctaPrimary: 'Find a Mentor',
    ctaSecondary: 'Become a Mentor',
    trust: ['Verified mentors', 'Secure payments', 'AI matching', 'Online sessions'],
    proofKicker: 'Why MifeLife?',
    sp1Title: 'Verified expert mentors',
    sp1Sub: 'Every mentor profile is manually reviewed and approved.',
    sp2Title: 'Secure payments — iyzico',
    sp2Sub: '256-bit SSL. Your card details are never stored with us.',
    sp3Title: 'AI-powered matching',
    sp3Sub: 'Find the mentor that fits your goal in seconds.',
    flowTitle: 'Meet your mentor in 3 steps',
    expTitle: 'Browse by expertise',
    expSub: 'Filter mentors specialized in exactly what you need, in one click.',
    s1: 'Find a mentor', s1d: 'Filter by expertise, language and budget. AI recommends the best fits.',
    s2: 'Book your session', s2d: 'Pick a time, pay securely. Your online session link is ready instantly.',
    s3: 'Move toward your goal', s3d: 'Get your roadmap from a real expert in a 1:1 online session.',
    humanKicker: 'HUMAN-CENTERED, AI-POWERED',
    humanTitle: 'AI matches, real mentors guide.',
    humanSub: 'MifeLife is not an “AI product” — it is a platform of experienced human mentors. AI only helps you find the right one. The value is in the session with your mentor.',
    mentorsTitle: 'Featured mentors',
    mentorsSub: 'Verified experts from Turkey and Europe. Ranked by AI fit score.',
    seeAll: 'See all mentors',
    finalCtaTitle: 'The right mentor at the right time changes everything.',
    finalCtaSub: 'Find an expert mentor that fits you and book your first session today.',
    finalCta: 'Find a Mentor',
    // --- Founder Trust ---
    founderKicker: 'FROM THE FOUNDER',
    founderName: 'Hakan Dalmış',
    founderRole: 'Founder · MifeLife',
    founderQuote: 'I built MifeLife because I believe meeting the right person at the right time changes lives. AI draws your roadmap — but real transformation begins in that one session with an experienced mentor.',
    founderTrust1: 'Every mentor is manually reviewed and approved',
    founderTrust2: 'Your payments are secured by iyzico',
    founderTrust3: 'Your data is protected, GDPR-compliant',
    // --- How it works (4 step) ---
    howTitle: 'How MifeLife works',
    howSub: 'Meet the mentor who takes you to your goal — in four simple steps.',
    how1: 'Analysis', how1d: 'A short 10-question analysis surfaces your priorities, strengths and gaps.',
    how2: 'Mentor matching', how2d: 'AI ranks the expert mentors that best fit your goal.',
    how3: 'Book a session', how3d: 'Pick a time, pay securely. Your online session link is ready instantly.',
    how4: 'Move toward your goal', how4d: 'Get your roadmap from a real expert in a 1:1 session, then act on it.',
    // --- Early Access Club ---
    eaKicker: 'EARLY ACCESS CLUB',
    eaTitle: 'Join the founding community',
    eaSub: 'Launch priority + a founder discount await you. Get early access to the first mentor drops and new features before anyone else.',
    eaBenefit1: 'Early access to the first mentor announcements',
    eaBenefit2: 'Be the first to know about new features',
    eaBenefit3: 'Exclusive perks for the founding community',
    eaPlaceholder: 'Your email address',
    eaCta: 'Join the club',
    eaSuccess: 'Welcome aboard! Check your inbox.',
    eaErr: 'Enter a valid email.',
    discountBadge: 'First session discount',
    eaProofMany: 'people joined the early access list',
    eaProofFew: 'Be one of the first to join the founding community.',
    eaYourNumber: 'Your founding member number',
  },
  de: {
    eyebrow: 'EXPERTEN-MENTOR-MARKTPLATZ',
    h1: 'Triff Experten-Mentoren, die dich begleiten',
    h1Accent: 'in Karriere, Business oder Startup.',
    sub: 'Buche 1:1-Online-Sessions mit erfahrenen Mentoren aus der Türkei und Europa. Finde den richtigen Mentor mit KI, plane sofort.',
    ctaPrimary: 'Mentor finden',
    ctaSecondary: 'Mentor werden',
    trust: ['Verifizierte Mentoren', 'Sichere Zahlung', 'KI-Matching', 'Online-Sessions'],
    proofKicker: 'Warum MifeLife?',
    sp1Title: 'Verifizierte Experten-Mentoren',
    sp1Sub: 'Jedes Mentor-Profil wird manuell geprüft und freigegeben.',
    sp2Title: 'Sichere Zahlung — iyzico',
    sp2Sub: '256-Bit-SSL. Deine Kartendaten werden nie bei uns gespeichert.',
    sp3Title: 'KI-gestütztes Matching',
    sp3Sub: 'Finde den passenden Mentor in Sekunden.',
    flowTitle: 'In 3 Schritten zu deinem Mentor',
    expTitle: 'Nach Fachgebiet entdecken',
    expSub: 'Filtere Mentoren mit genau der Expertise, die du brauchst — mit einem Klick.',
    s1: 'Mentor finden', s1d: 'Filtere nach Expertise, Sprache und Budget. KI empfiehlt die besten.',
    s2: 'Session buchen', s2d: 'Wähle einen Termin, zahle sicher. Dein Online-Link ist sofort bereit.',
    s3: 'Ziel erreichen', s3d: 'Hol dir deine Roadmap von einem echten Experten in einer 1:1-Session.',
    humanKicker: 'MENSCHZENTRIERT, KI-GESTÜTZT',
    humanTitle: 'KI matcht, echte Mentoren begleiten.',
    humanSub: 'MifeLife ist kein „KI-Produkt“ — es ist eine Plattform erfahrener Mentoren. KI hilft dir nur, den richtigen zu finden. Der Wert liegt in der Session.',
    mentorsTitle: 'Top-Mentoren',
    mentorsSub: 'Verifizierte Experten aus der Türkei und Europa. Nach KI-Fit-Score sortiert.',
    seeAll: 'Alle Mentoren ansehen',
    finalCtaTitle: 'Der richtige Mentor zur richtigen Zeit verändert alles.',
    finalCtaSub: 'Finde einen passenden Experten-Mentor und buche heute deine erste Session.',
    finalCta: 'Mentor finden',
    // --- Founder Trust ---
    founderKicker: 'VOM GRÜNDER',
    founderName: 'Hakan Dalmış',
    founderRole: 'Gründer · MifeLife',
    founderQuote: 'Ich habe MifeLife gegründet, weil ich glaube, dass die richtige Person zur richtigen Zeit Leben verändert. KI zeichnet deine Roadmap — doch echte Veränderung beginnt in dieser einen Session mit einem erfahrenen Mentor.',
    founderTrust1: 'Jeder Mentor wird manuell geprüft und freigegeben',
    founderTrust2: 'Deine Zahlungen sind durch iyzico gesichert',
    founderTrust3: 'Deine Daten sind DSGVO-konform geschützt',
    // --- How it works (4 step) ---
    howTitle: 'So funktioniert MifeLife',
    howSub: 'Triff in vier einfachen Schritten den Mentor, der dich zu deinem Ziel bringt.',
    how1: 'Analyse', how1d: 'Eine kurze 10-Fragen-Analyse zeigt deine Prioritäten, Stärken und Lücken.',
    how2: 'Mentor-Matching', how2d: 'KI sortiert die Experten-Mentoren, die am besten zu deinem Ziel passen.',
    how3: 'Session buchen', how3d: 'Wähle einen Termin, zahle sicher. Dein Online-Link ist sofort bereit.',
    how4: 'Ziel erreichen', how4d: 'Hol dir deine Roadmap von einem echten Experten in einer 1:1-Session und setze sie um.',
    // --- Early Access Club ---
    eaKicker: 'EARLY ACCESS CLUB',
    eaTitle: 'Werde Teil der Gründer-Community',
    eaSub: 'Launch-Priorität + Gründer-Rabatt warten auf dich. Erhalte als Erster Zugang zu den ersten Mentoren und neuen Funktionen.',
    eaBenefit1: 'Früher Zugang zu den ersten Mentor-Ankündigungen',
    eaBenefit2: 'Erfahre als Erster von neuen Funktionen',
    eaBenefit3: 'Exklusive Vorteile für die Gründer-Community',
    eaPlaceholder: 'Deine E-Mail-Adresse',
    eaCta: 'Dem Club beitreten',
    eaSuccess: 'Willkommen! Prüfe dein Postfach.',
    eaErr: 'Gib eine gültige E-Mail ein.',
    discountBadge: 'Erste-Session-Rabatt',
    eaProofMany: 'Personen sind der Early-Access-Liste beigetreten',
    eaProofFew: 'Sei eine/r der Ersten in der Gründer-Community.',
    eaYourNumber: 'Deine Gründungsmitglied-Nummer',
  },
};

const TRUST_ICONS = [BadgeCheck, ShieldCheck, Sparkles, Video];

// Expertise categories → link into the marketplace pre-filtered by specialty.
// `key` must match the specialty strings stored on mentor records.
const EXPERTISE = [
  { key: 'Kariyer', icon: Briefcase, tr: 'Kariyer', en: 'Career', de: 'Karriere' },
  { key: 'Girişimcilik', icon: Rocket, tr: 'Girişimcilik', en: 'Entrepreneurship', de: 'Unternehmertum' },
  { key: 'Yazılım', icon: Code2, tr: 'Yazılım', en: 'Software', de: 'Software' },
  { key: 'Finans', icon: LineChart, tr: 'Finans', en: 'Finance', de: 'Finanzen' },
  { key: 'Pazarlama', icon: Megaphone, tr: 'Pazarlama', en: 'Marketing', de: 'Marketing' },
  { key: 'Liderlik', icon: Crown, tr: 'Liderlik', en: 'Leadership', de: 'Führung' },
  { key: 'Satış', icon: TrendingUp, tr: 'Satış', en: 'Sales', de: 'Vertrieb' },
  { key: 'Veri Bilimi', icon: BarChart3, tr: 'Veri Bilimi', en: 'Data Science', de: 'Data Science' },
  { key: 'UX/UI', icon: PenTool, tr: 'UX/UI', en: 'UX/UI', de: 'UX/UI' },
  { key: 'İşletme', icon: Building2, tr: 'İşletme', en: 'Business', de: 'Business' },
];

export default function V2LandingPage() {
  const [lang, setLang] = useState('tr');
  const T = COPY[lang] || COPY.tr;
  const [mentors, setMentors] = useState([]);
  const [eaStats, setEaStats] = useState(null);

  useEffect(() => {
    apiRequest('/v2/mentors/').then((r) => setMentors((r?.items || []).slice(0, 6))).catch(() => {});
    apiRequest('/v2/early-access/stats').then(setEaStats).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = 'MifeLife · ' + T.h1 + ' ' + T.h1Accent;
    document.documentElement.setAttribute('lang', lang);
  }, [lang, T.h1, T.h1Accent]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0F0F1A] via-[#11111E] to-[#1A1A2E] text-white antialiased" data-testid="v2-landing-page">
      <V2Nav lang={lang} onLangChange={setLang} />

      {/* ---------- HERO ---------- */}
      <section className="pt-32 sm:pt-40 pb-20 sm:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/3 w-[600px] h-[600px] rounded-full bg-violet-500/10 blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/30 text-[11px] uppercase tracking-[0.18em] text-violet-200 mb-6" data-testid="v2-hero-eyebrow">
              <Sparkles className="w-3 h-3" />
              {T.eyebrow}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]" data-testid="v2-hero-h1">
              {T.h1}<br />
              <span className="bg-gradient-to-r from-violet-300 via-fuchsia-300 to-cyan-300 bg-clip-text text-transparent">{T.h1Accent}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed" data-testid="v2-hero-sub">{T.sub}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/mentorler"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition shadow-2xl shadow-violet-500/30 group"
                data-testid="v2-hero-cta-primary"
              >
                <Search className="w-4 h-4" />
                {T.ctaPrimary}
                <ArrowRight className="w-4 h-4 transition group-hover:translate-x-1" />
              </Link>
              <Link
                to="/mentor-basvuru"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl border border-white/15 bg-white/[0.03] text-slate-100 text-sm font-semibold hover:bg-white/[0.07] hover:border-white/25 transition"
                data-testid="v2-hero-cta-secondary"
              >
                {T.ctaSecondary}
              </Link>
            </div>
            {/* Trust row */}
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2" data-testid="v2-hero-trust">
              {T.trust.map((label, i) => {
                const Icon = TRUST_ICONS[i];
                return (
                  <span key={label} className="inline-flex items-center gap-1.5 text-xs text-slate-400">
                    <Icon className="w-3.5 h-3.5 text-violet-300" strokeWidth={2} /> {label}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Hero visual — real human mentoring photo + trust overlay + avatars */}
          <div className="lg:col-span-5 hidden lg:block" data-testid="v2-hero-visual">
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40 aspect-[4/5]">
                <img src={HERO_IMG} alt="Mentor görüşmesi" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F1A] via-transparent to-transparent" />
                {/* Glass trust card */}
                <div className="absolute left-4 right-4 bottom-4 rounded-2xl border border-white/15 bg-white/[0.08] backdrop-blur-md p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex -space-x-2.5">
                      {(mentors || []).slice(0, 4).map((m) => (
                        <img key={m.id} src={m.photo_url} alt={m.name} className="w-9 h-9 rounded-full border-2 border-[#11111E] object-cover" />
                      ))}
                    </div>
                    <div className="text-right">
                      <div className="flex items-center justify-end gap-1 text-amber-300">
                        <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                        <span className="text-sm font-semibold tabular-nums">4.8</span>
                      </div>
                      <div className="text-[10px] text-slate-300">{lang === 'en' ? 'avg. session rating' : (lang === 'de' ? 'Ø Session-Bewertung' : 'ortalama seans puanı')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- TRUST / WHY STRIP ---------- */}
      <section className="py-14 border-y border-white/[0.06] bg-[#0F0F1A]/40" data-testid="v2-landing-why">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-8">{T.proofKicker}</div>
          <div className="grid sm:grid-cols-3 gap-5">
            <WhyCell icon={BadgeCheck} title={T.sp1Title} sub={T.sp1Sub} />
            <WhyCell icon={ShieldCheck} title={T.sp2Title} sub={T.sp2Sub} />
            <WhyCell icon={Sparkles} title={T.sp3Title} sub={T.sp3Sub} />
          </div>
        </div>
      </section>

      {/* ---------- FOUNDER TRUST ---------- */}
      <section className="py-20 sm:py-24 border-t border-white/[0.06] relative overflow-hidden" data-testid="v2-landing-founder">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/4 w-[480px] h-[280px] rounded-full bg-violet-500/[0.07] blur-[110px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-10 grid md:grid-cols-[auto,1fr] gap-7 sm:gap-10 items-start">
            <div className="flex md:block items-center gap-4">
              <img src={FOUNDER_IMG} alt={T.founderName} className="w-20 h-20 sm:w-28 sm:h-28 rounded-full object-cover border-2 border-violet-400/30 flex-shrink-0" data-testid="v2-founder-photo" />
              <div className="md:mt-4">
                <div className="text-base font-semibold text-white">{T.founderName}</div>
                <div className="text-xs text-slate-400">{T.founderRole}</div>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-3">{T.founderKicker}</div>
              <Quote className="w-7 h-7 text-violet-400/40 mb-3" />
              <p className="text-lg sm:text-xl text-slate-100 leading-relaxed font-medium" data-testid="v2-founder-quote">{T.founderQuote}</p>
              <div className="mt-7 grid sm:grid-cols-3 gap-3">
                {[
                  [BadgeCheck, T.founderTrust1],
                  [ShieldCheck, T.founderTrust2],
                  [Lock, T.founderTrust3],
                ].map(([Icon, label], i) => (
                  <div key={i} className="flex items-start gap-2.5 rounded-xl border border-white/[0.07] bg-white/[0.02] p-3.5" data-testid={`v2-founder-trust-${i}`}>
                    <Icon className="w-4 h-4 text-violet-300 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="text-[13px] text-slate-300 leading-snug">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- EXPERTISE CATEGORIES ---------- */}
      <section className="py-20 sm:py-24 border-t border-white/[0.06]" data-testid="v2-landing-expertise">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="mb-10">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">{T.expTitle}</h2>
            <p className="mt-3 text-base text-slate-400 max-w-2xl">{T.expSub}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {EXPERTISE.map((e) => (
              <ExpertiseCard key={e.key} e={e} lang={lang} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------- HOW IT WORKS (4 STEP) ---------- */}
      <section className="py-20 sm:py-28" data-testid="v2-how-it-works">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">{T.howTitle}</h2>
            <p className="mt-3 text-base text-slate-400">{T.howSub}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <HowStep num="01" icon={Brain} title={T.how1} desc={T.how1d} testid="v2-how-1" />
            <HowStep num="02" icon={Sparkles} title={T.how2} desc={T.how2d} testid="v2-how-2" />
            <HowStep num="03" icon={CalendarCheck} title={T.how3} desc={T.how3d} testid="v2-how-3" />
            <HowStep num="04" icon={Target} title={T.how4} desc={T.how4d} testid="v2-how-4" />
          </div>
        </div>
      </section>

      {/* ---------- HUMAN-CENTERED BAND ---------- */}
      <section className="py-20 border-t border-white/[0.06] relative overflow-hidden" data-testid="v2-landing-human">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-0 w-[500px] h-[300px] rounded-full bg-violet-500/[0.07] blur-[110px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div className="grid grid-cols-2 gap-3 order-2 lg:order-1">
            <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=500&h=600&fit=crop&q=80" alt="Mentor & mentee" className="rounded-2xl border border-white/10 object-cover w-full h-64" />
            <img src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=500&h=600&fit=crop&q=80" alt="Online görüşme" className="rounded-2xl border border-white/10 object-cover w-full h-64 mt-8" />
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-3">{T.humanKicker}</div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">{T.humanTitle}</h2>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">{T.humanSub}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/mentorler" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition" data-testid="v2-human-cta">
                {T.ctaPrimary} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- MENTORS PREVIEW ---------- */}
      <section className="py-20 border-t border-white/[0.06]" data-testid="v2-landing-mentors">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {T.eyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">{T.mentorsTitle}</h2>
              <p className="mt-3 text-base text-slate-400 max-w-2xl">{T.mentorsSub}</p>
            </div>
            <Link to="/mentorler" className="inline-flex items-center gap-1.5 text-sm text-violet-200 hover:text-white transition" data-testid="v2-landing-mentors-all">
              {T.seeAll} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(mentors || []).slice(0, 6).map((m) => (
              <MentorPreview key={m.id} m={m} lang={lang} discountLabel={T.discountBadge} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------- COMPANY MODE TEASER ---------- */}
      <section className="py-20 border-t border-white/[0.06] relative overflow-hidden" data-testid="v2-landing-company">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 right-0 w-[500px] h-[300px] rounded-full bg-emerald-500/[0.06] blur-[100px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-300 mb-3">{lang === 'en' ? 'FOR YOUR COMPANY' : (lang === 'de' ? 'FÜR IHR UNTERNEHMEN' : 'ŞİRKETİNİZ İÇİN')}</div>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
                {lang === 'en' ? 'A health check for your business — meet a real expert.' : (lang === 'de' ? 'Ein Gesundheitscheck für Ihr Unternehmen — finden Sie einen Experten.' : 'Şirketin için sağlık taraması — gerçek uzmana bağlan.')}
              </h2>
              <p className="mt-4 text-base text-slate-400 leading-relaxed">
                {lang === 'en' ? '8 questions across Finance, Marketing, Sales, Operations. AI maps your company profile and recommends a mentor in your weakest dimension.' : (lang === 'de' ? '8 Fragen zu Finanzen, Marketing, Vertrieb, Operationen. KI erstellt Ihr Profil und schlägt einen Experten vor.' : 'Finans, Pazarlama, Satış, Operasyon. AI şirket profilinizi çıkarır, en zayıf boyutta uzman mentor önerir.')}
              </p>
              <Link
                to="/sirket"
                className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-lg border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 text-sm font-semibold hover:bg-emerald-500/20 transition"
                data-testid="v2-landing-company-cta"
              >
                {lang === 'en' ? 'Run company scan →' : (lang === 'de' ? 'Unternehmens-Scan starten →' : 'Şirket sağlık taraması yap →')}
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Finans', 'Finance', 'Finanzen', 'bg-emerald-500/10 border-emerald-400/30 text-emerald-300'],
                ['Pazarlama', 'Marketing', 'Marketing', 'bg-amber-500/10 border-amber-400/30 text-amber-300'],
                ['Satış', 'Sales', 'Vertrieb', 'bg-blue-500/10 border-blue-400/30 text-blue-300'],
                ['Operasyon', 'Operations', 'Operationen', 'bg-violet-500/10 border-violet-400/30 text-violet-300'],
              ].map(([tr, en, de, cls], i) => (
                <div key={i} className={`rounded-xl border p-4 ${cls}`}>
                  <div className="text-sm font-semibold">{lang === 'en' ? en : (lang === 'de' ? de : tr)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- EARLY ACCESS CLUB ---------- */}
      <section className="py-20 sm:py-24 border-t border-white/[0.06] relative overflow-hidden" data-testid="v2-early-access">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[320px] rounded-full bg-fuchsia-500/[0.08] blur-[120px]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8">
          <div className="rounded-3xl border border-violet-400/20 bg-gradient-to-br from-violet-500/[0.08] to-fuchsia-500/[0.04] p-7 sm:p-10">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-fuchsia-200 mb-4">
                  <Crown className="w-3.5 h-3.5" /> {T.eaKicker}
                </div>
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">{T.eaTitle}</h2>
                <p className="mt-3 text-base text-slate-300 leading-relaxed">{T.eaSub}</p>
                <div className="mt-3 inline-flex items-center gap-2 text-xs text-slate-400" data-testid="v2-ea-socialproof">
                  <Users className="w-3.5 h-3.5 text-fuchsia-300" />
                  {eaStats?.show_public
                    ? <span><span className="text-white font-semibold">{eaStats.rounded}+</span> {T.eaProofMany}</span>
                    : <span>{T.eaProofFew}</span>}
                </div>
                <ul className="mt-5 space-y-2.5">
                  {[T.eaBenefit1, T.eaBenefit2, T.eaBenefit3].map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-200" data-testid={`v2-ea-benefit-${i}`}>
                      <CheckCircle2 className="w-4 h-4 text-fuchsia-300 flex-shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <EarlyAccessForm lang={lang} T={T} />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section className="py-24 sm:py-32 text-center relative overflow-hidden" data-testid="v2-landing-final-cta">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[300px] rounded-full bg-violet-500/10 blur-[120px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
          <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight leading-tight">{T.finalCtaTitle}</h2>
          <p className="mt-5 text-base text-slate-400">{T.finalCtaSub}</p>
          <Link
            to="/mentorler"
            className="mt-9 inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition shadow-2xl shadow-violet-500/30 group"
            data-testid="v2-landing-final-cta-btn"
          >
            {T.finalCta} <ArrowRight className="w-4 h-4 transition group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <V2Footer lang={lang} />
    </div>
  );
}

const WhyCell = ({ icon: Icon, title, sub }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.04] transition">
    <span className="inline-flex w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-400/30 items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-violet-300" strokeWidth={1.8} />
    </span>
    <div className="text-base font-semibold text-white">{title}</div>
    <div className="text-sm text-slate-400 mt-1.5 leading-relaxed">{sub}</div>
  </div>
);

const ExpertiseCard = ({ e, lang }) => {
  const Icon = e.icon;
  const label = e[lang] || e.tr;
  return (
    <Link
      to={`/mentorler?specialty=${encodeURIComponent(e.key)}`}
      className="group rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:bg-white/[0.05] hover:border-violet-400/40 transition flex flex-col items-start gap-3"
      data-testid={`v2-expertise-${e.key}`}
    >
      <span className="inline-flex w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500/20 to-cyan-500/10 border border-white/10 items-center justify-center group-hover:scale-105 transition">
        <Icon className="w-5 h-5 text-violet-200" strokeWidth={1.7} />
      </span>
      <span className="text-sm font-semibold text-white">{label}</span>
      <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 group-hover:text-violet-300 transition mt-auto">
        {lang === 'en' ? 'View mentors' : (lang === 'de' ? 'Mentoren ansehen' : 'Mentörleri gör')}
        <ArrowRight className="w-3 h-3 transition group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
};

const HowStep = ({ num, icon: Icon, title, desc, testid }) => (
  <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.04] hover:border-violet-400/30 transition" data-testid={testid}>
    <div className="flex items-center justify-between mb-5">
      <span className="inline-flex w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/10 border border-white/10 items-center justify-center">
        <Icon className="w-5 h-5 text-violet-200" strokeWidth={1.7} />
      </span>
      <span className="text-2xl font-semibold text-white/10 tabular-nums">{num}</span>
    </div>
    <h3 className="text-base font-semibold tracking-tight mb-1.5">{title}</h3>
    <p className="text-[13px] text-slate-400 leading-relaxed">{desc}</p>
  </div>
);

const EarlyAccessForm = ({ lang, T }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [position, setPosition] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { toast.error(T.eaErr); return; }
    setLoading(true);
    try {
      const res = await apiRequest('/v2/early-access', {
        method: 'POST',
        body: JSON.stringify({ email: v, locale: lang, source: 'homepage' }),
      });
      setPosition(res?.position ?? null);
      setDone(true);
      toast.success(res?.message || T.eaSuccess);
    } catch (err) {
      toast.error(err?.message || T.eaErr);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/[0.08] p-6 text-center" data-testid="v2-ea-success">
        <CheckCircle2 className="w-8 h-8 text-emerald-300 mx-auto mb-3" />
        <div className="text-base font-semibold text-white">{T.eaSuccess}</div>
        {position != null && (
          <div className="mt-4 inline-flex flex-col items-center gap-0.5 px-5 py-3 rounded-xl bg-violet-500/15 border border-violet-400/30" data-testid="v2-ea-position">
            <span className="text-[10px] uppercase tracking-[0.18em] text-violet-200/80">{T.eaYourNumber}</span>
            <span className="text-2xl font-bold text-violet-100 tabular-nums">#{position}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="v2-ea-form">
      <div className="relative">
        <Mail className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={T.eaPlaceholder}
          className="w-full h-12 pl-10 pr-4 rounded-xl bg-[#0a0a14] border border-white/15 text-sm text-white placeholder:text-slate-500 focus:border-violet-400/60 focus:outline-none transition"
          data-testid="v2-ea-email"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-semibold hover:from-violet-400 hover:to-fuchsia-400 transition disabled:opacity-50 shadow-xl shadow-violet-500/20"
        data-testid="v2-ea-submit"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
        {T.eaCta}
      </button>
    </form>
  );
};

const MentorPreview = ({ m, lang, discountLabel }) => {
  const disc = m.first_session_discount;
  const hasDisc = disc?.enabled && Number(disc.pct) >= 5;
  return (
  <Link to={`/m/${m.slug || m.id}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:bg-white/[0.04] hover:border-white/20 transition flex items-start gap-4" data-testid={`v2-landing-mentor-${m.id}`}>
    <img src={m.photo_url} alt={m.name} className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-semibold tracking-tight">{m.name}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-200 border border-violet-400/30">AI {m.ai_match_pct}%</span>
      </div>
      <div className="text-xs text-slate-400 mb-2">{m.title}</div>
      {hasDisc && (
        <div className="inline-flex items-center gap-1 mb-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200" data-testid={`v2-landing-mentor-discount-${m.id}`}>
          <Sparkles className="w-2.5 h-2.5" />{discountLabel} %{disc.pct}
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        {(m.specialties || []).slice(0, 2).map((s) => (
          <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-slate-300">{s}</span>
        ))}
      </div>
    </div>
  </Link>
  );
};
